import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Brain, Plus, Trash2, Trophy, Clock, ChevronRight, RotateCw, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { memorizationApi } from '../services/api';
import { memorizationAudioStorage } from '../audio/memorizationAudioStorage';
import { Button } from '../components/ui/button';
import { SectionHeader, PageLoading, GlassCard, EmptyState } from '../components/shared';
import { toast } from 'sonner';

const STATUS_KEYS = {
  learning: 'memorization.status_learning',
  needs_review: 'memorization.status_needs_review',
  validated: 'memorization.status_validated',
  mastered: 'memorization.status_mastered',
};
const STATUS_COLOR = {
  learning: 'text-amber-300 bg-amber-900/30',
  needs_review: 'text-orange-300 bg-orange-900/30',
  validated: 'text-blue-300 bg-blue-900/30',
  mastered: 'text-emerald-300 bg-emerald-900/30',
};

const formatDate = (s) => {
  if (!s) return null;
  try {
    return new Date(s).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return s;
  }
};

const PlanCard = ({ plan, onDelete, isRevision = false }) => {
  const { t } = useTranslation();
  const statusKey = plan.memorization_status in STATUS_KEYS ? plan.memorization_status : 'learning';
  const statusLabel = t(STATUS_KEYS[statusKey]);
  const statusColor = STATUS_COLOR[statusKey];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5 hover:border-primary/40 transition-all"
      data-testid={`memo-plan-card-${plan.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <Link
            to={`/memorization/${plan.id}`}
            className="text-lg font-serif text-text-primary hover:text-primary transition-colors block truncate"
            data-testid={`memo-plan-link-${plan.id}`}
          >
            {plan.title}
          </Link>
          <p className="text-xs text-text-muted mt-1">
            {t('memorization.surah')} {plan.surah_number} · {plan.from_ayah}–{plan.to_ayah} · {plan.total_ayahs}
          </p>
        </div>
        <span className={`text-xs px-2 py-1 rounded ${statusColor}`}>{statusLabel}</span>
      </div>

      <div className="flex items-center gap-3 text-xs text-text-muted mb-4">
        {plan.best_score != null && (
          <span className="flex items-center gap-1">
            <Trophy className="w-3.5 h-3.5 text-primary" />
            {t('memorization.best')} : <span className="text-text-primary font-medium">{plan.best_score}</span>/100
          </span>
        )}
        {plan.last_score != null && (
          <span>{t('memorization.last')} : {plan.last_score}/100</span>
        )}
        {plan.next_review_at && (
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatDate(plan.next_review_at)}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Link to={`/memorization/${plan.id}`} className="flex-1">
          <Button
            variant="default"
            size="sm"
            className="w-full bg-primary hover:bg-primary/90 text-background"
            data-testid={`memo-plan-open-${plan.id}`}
          >
            {isRevision ? <RotateCw className="w-4 h-4 mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
            {isRevision ? t('memorization.review') : t('memorization.continue')}
          </Button>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(plan.id)}
          className="text-text-muted hover:text-danger"
          data-testid={`memo-plan-delete-${plan.id}`}
          aria-label={t('common.delete')}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

export default function Memorization() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [p, r] = await Promise.all([
        memorizationApi.listPlans(),
        memorizationApi.revisions(),
      ]);
      setPlans(p.data || []);
      setRevisions(r.data || []);
    } catch (e) {
      toast.error(t('common.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('memorization.delete_confirm'))) return;
    try {
      await memorizationApi.deletePlan(id);
      try { await memorizationAudioStorage.deleteByPlan(id); } catch {}
      toast.success(t('memorization.deleted'));
      refresh();
    } catch {
      toast.error(t('memorization.delete_failed'));
    }
  };

  if (authLoading) return <PageLoading />;
  if (!user) return <Navigate to="/login?next=/memorization" replace />;
  if (loading) return <PageLoading />;

  const revisionIds = new Set(revisions.map((r) => r.id));
  const otherPlans = plans.filter((p) => !revisionIds.has(p.id));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="memo-home">
      <div className="flex items-start justify-between gap-4 mb-8">
        <SectionHeader
          overline={t('nav.memorization')}
          title={t('memorization.title')}
          subtitle={t('memorization.subtitle')}
        />
        <Button
          onClick={() => navigate('/memorization/new')}
          className="bg-primary hover:bg-primary/90 text-background flex-shrink-0"
          data-testid="memo-new-plan-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('memorization.new_plan')}
        </Button>
      </div>

      {revisions.length > 0 && (
        <section className="mb-12" data-testid="memo-revisions-section">
          <div className="flex items-center gap-2 mb-4">
            <RotateCw className="w-5 h-5 text-primary" />
            <h3 className="text-xl font-serif text-text-primary">{t('memorization.to_review_today')}</h3>
            <span className="text-xs text-text-muted">({revisions.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {revisions.map((p) => (
              <PlanCard key={p.id} plan={p} onDelete={handleDelete} isRevision />
            ))}
          </div>
        </section>
      )}

      <section data-testid="memo-plans-section">
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-serif text-text-primary">{t('memorization.my_plans')}</h3>
          <span className="text-xs text-text-muted">({plans.length})</span>
        </div>
        {plans.length === 0 ? (
          <GlassCard className="text-center py-16">
            <Sparkles className="w-12 h-12 text-primary/60 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-text-primary mb-2">
              {t('memorization.empty_title')}
            </h4>
            <p className="text-sm text-text-secondary mb-6 max-w-md mx-auto">
              {t('memorization.empty_subtitle')}
            </p>
            <Button
              onClick={() => navigate('/memorization/new')}
              className="bg-primary hover:bg-primary/90 text-background"
              data-testid="memo-empty-create-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('memorization.create_plan')}
            </Button>
          </GlassCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherPlans.map((p) => (
              <PlanCard key={p.id} plan={p} onDelete={handleDelete} />
            ))}
            {otherPlans.length === 0 && (
              <p className="text-text-muted text-sm col-span-full">
                {t('memorization.all_in_review_today')}
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
